import { useState } from 'react'
import { Controller } from 'react-hook-form'
import { Client } from '../../../pure-js/types/GrooverTypes'
import { FOUR_PX } from '../enums/Spacings'
import { State } from '../hooks/useAppState'
import { useCurrency } from '../hooks/useCurrency'
import { useForm } from '../hooks/useForm'
import { addSsnForEnrollmentUser } from '../libs/CloudFunctionsApiHandler'
import { formatGrooverText, getGrooverText } from '../libs/GrooverTextRepository'
import Box from './Box'
import { getReceiptSsnValidationSchema } from './CourseRegistrationStepFour'
import { ContinueButton } from './StyledComponents'
import { TextFieldWithLabelForm } from './TextFieldWithLabelForm'
import iconArrow from '/public/iconArrow.svg'
import GrooverText from './GrooverText'
import { usePortalAuth } from '../hooks/usePortalAuth'

type Props = {
  state: State
  client: Client
  userType: 'mainUser' | 'partnerUser'
  onStoreSsn: (userSsn: string, userType: 'mainUser' | 'partnerUser') => void
}

const CourseRegistrationReceiptSsn = ({ state, client, userType, onStoreSsn }: Props) => {
  const { isViewAs } = usePortalAuth()
  const [isLoadingSsn, setIsLoadingSsn] = useState(false)
  const currency = useCurrency(client)
  const ssnFormProps = useForm<{ userSsnField: string }>(getReceiptSsnValidationSchema(currency), {
    defaultValues: {
      userSsnField: userType === 'mainUser' ? state.mainUserSsn : state.partnerUserSsn
    },
    mode: 'all', // This enables validation on blur
    reValidateMode: 'onChange' // This re-validates on change after initial
  })

  const handleAddSsn = (ssn: string, userType: 'mainUser' | 'partnerUser') => {
    setIsLoadingSsn(true)

    onStoreSsn(ssn, userType)

    addSsnForEnrollmentUser({
      clientId: client?.id as string,
      enrollmentId: state.obfuscatedEnrollment?.id ? state.obfuscatedEnrollment?.id : state.enrollmentId,
      ssn: ssn,
      userType: userType
    })
      .then(() => {
        console.log('SSN added')
        setIsLoadingSsn(false)
      })
      .catch((err) => {
        console.log(err)
        setIsLoadingSsn(false)
      })
      .finally(() => {
        setIsLoadingSsn(false)
      })
  }

  const userSsnPlaceholder =
    userType === 'mainUser'
      ? state.obfuscatedEnrollment?.mainUser?.nonObfuscatedName
        ? state.obfuscatedEnrollment?.mainUser?.nonObfuscatedName
        : state.name
      : state.obfuscatedEnrollment?.partnerUser?.nonObfuscatedName
        ? state.obfuscatedEnrollment?.partnerUser?.nonObfuscatedName
        : state.partnerName

  return (
    <>
      <Box fullWidth direction="row" align="center" justify="space-between" top spacing={FOUR_PX} gap={FOUR_PX}>
        <Controller
          control={ssnFormProps.control}
          name="userSsnField"
          render={({ field, fieldState }) => (
            <TextFieldWithLabelForm
              {...field}
              value={field.value}
              fullWidth
              fieldState={fieldState}
              placeholder={formatGrooverText(getGrooverText('registrationStep5PaymentInputAddPersonalNumberHelper'), [
                userSsnPlaceholder
              ])}
            />
          )}
        />
        <ContinueButton
          isDisabled={isLoadingSsn || isViewAs}
          disabled={isLoadingSsn || isViewAs}
          onClick={() => {
            if (isViewAs) return
            handleAddSsn(ssnFormProps.getValues('userSsnField'), userType)
          }}
        >
          <GrooverText textKey="registrationCtactaContinue" />
          <img src={iconArrow} width={16} height={16} />
        </ContinueButton>
      </Box>
    </>
  )
}

export default CourseRegistrationReceiptSsn
